"use client";

import { useState } from "react";
import { Heart, Plus } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Pagination, PaginationContent, PaginationItem, PaginationLink } from "@/components/ui/pagination";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { AddToCartButton, EcommerceProductsProvider, ProductSortDropdown, ProductsFilter, useEcommerceProducts } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
declare const wvcClient: any;
function ProductCard({
  product,
  index
}: {
  product: any;
  index: number;
}) {
  const [wishlisted, setWishlisted] = useState(false);
  return <div data-index={index} className="group flex flex-col">
      <div className="relative overflow-hidden rounded-lg bg-card aspect-square border border-border">
        <Link to={product.link} className="block h-full w-full">
          {product.featured_image_url && <img src={product.featured_image_url} alt={product.title} className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-105" loading="lazy" decoding="async" />}
        </Link>

        <Button type="button" variant="ghost" size="icon" onClick={() => setWishlisted(!wishlisted)} aria-label="Add to wishlist" className="absolute top-4 right-4 size-10 rounded-lg bg-background/80 backdrop-blur-sm hover:bg-background">
          <Heart className={wishlisted ? "size-4 fill-primary text-primary" : "size-4 text-foreground"} />
        </Button>

        <div className="absolute bottom-4 left-4 right-4 opacity-0 translate-y-2 transition-all duration-300 group-hover:opacity-100 group-hover:translate-y-0">
          <AddToCartButton productId={product.id} variant="default" size="sm" className="w-full" buttonClassName="bg-primary text-primary-foreground w-full rounded-lg font-default" text="BUY NOW" showQuantity={false} />
        </div>
      </div>

      <div className="mt-5 px-1">
        {product.categories && product.categories.length > 0 && <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-default">
            {product.categories[0].name}
          </p>}
        <Link to={product.link}>
          <h3 className="mt-1.5 font-default text-xl text-foreground leading-tight hover:underline underline-offset-4">
            {product.title}
          </h3>
        </Link>
        <div className="mt-2 flex items-baseline gap-2">
          {product.discounted_price ? <>
              <span className="font-default text-base text-foreground">
                ${product.discounted_price}
              </span>
              <span className="font-default text-sm text-muted-foreground line-through">
                ${product.price}
              </span>
            </> : <span className="font-default text-base text-foreground">
              ${product.price}
            </span>}
        </div>
      </div>
    </div>;
}
function ProductsGridConsumer() {
  const {
    products,
    loading,
    error,
    refetch,
    currentPage,
    totalPages,
    hasNext,
    hasPrev,
    nextPage,
    prevPage,
    setPage
  } = useEcommerceProducts();
  if (loading) {
    return <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
        {[...Array(6)].map((_, i) => <div key={i} data-index={i} className="flex flex-col">
            <div className="aspect-square rounded-lg bg-muted/40 animate-pulse" />
            <div className="mt-5 space-y-2">
              <div className="h-3 w-20 bg-muted/40 rounded-md animate-pulse" />
              <div className="h-5 w-3/4 bg-muted/40 rounded-md animate-pulse" />
              <div className="h-4 w-16 bg-muted/40 rounded-md animate-pulse" />
            </div>
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="text-center py-16">
        <p className="font-default text-lg text-muted-foreground">
          Unable to load mobile devices.
        </p>
        <Button variant="link" onClick={refetch} className="mt-2 italic font-default text-primary">
          Retry connection
        </Button>
      </div>;
  }
  if (products.length === 0) {
    return <div className="text-center py-16">
        <p className="font-default text-lg text-muted-foreground">
          No devices match your current filter criteria.
        </p>
      </div>;
  }
  const pageNumbers: number[] = [];
  const maxVisible = 5;
  const startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
  const endPage = Math.min(totalPages, startPage + maxVisible - 1);
  for (let p = startPage; p <= endPage; p++) {
    pageNumbers.push(p);
  }
  return <>
      <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
        {products.map((product, i) => <ProductCard key={product.id} product={product} index={i} />)}
      </div>

      {totalPages > 1 && <div className="mt-16 border-t border-border pt-8">
          <Pagination>
            <PaginationContent className="w-full flex items-center justify-between">
              <PaginationItem>
                <Button variant="link" onClick={prevPage} disabled={!hasPrev} className="italic font-default text-foreground text-base px-0">
                  ← previous
                </Button>
              </PaginationItem>

              <div className="hidden sm:flex items-center gap-2">
                {pageNumbers.map((p, idx) => <PaginationItem key={p} data-index={idx}>
                    <PaginationLink onClick={() => setPage(p)} isActive={p === currentPage} className={p === currentPage ? "font-default text-base text-primary border-b border-primary rounded-none bg-transparent" : "font-default text-base text-muted-foreground hover:text-foreground bg-transparent border-transparent"}>
                      {p}
                    </PaginationLink>
                  </PaginationItem>)}
              </div>

              <div className="sm:hidden font-default text-sm text-muted-foreground">
                Page {currentPage} of {totalPages}
              </div>

              <PaginationItem>
                <Button variant="link" onClick={nextPage} disabled={!hasNext} className="italic font-default text-foreground text-base px-0">
                  next →
                </Button>
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>}
    </>;
}
export default function ShopContent() {
  const current_wp_query_params = typeof window !== "undefined" && (window as any).wvcClient ? (window as any).wvcClient.getCurrentWPQueryParams() : {};
  const wp_query = new WP_Query({
    ...current_wp_query_params,
    per_page: 9
  });
  return <section data-section-id="2742" id="shop-content" className="bg-background text-foreground py-16 lg:py-24">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="mb-12 lg:mb-16 max-w-2xl">
          <p className="text-[11px] uppercase tracking-[0.22em] text-primary font-default">
            TechWire Inventory
          </p>
          <h1 className="mt-4 font-default text-4xl md:text-5xl lg:text-6xl text-foreground tracking-tighter">
            Precision mobile hardware.
          </h1>
          <p className="mt-5 font-default text-base md:text-lg text-muted-foreground leading-relaxed">
            Explore our curated selection of flagship smartphones, high-performance tablets, and essential mobile accessories.
          </p>
        </div>

        <EcommerceProductsProvider wp_query={wp_query}>
          <div className="flex flex-col lg:flex-row gap-10 lg:gap-12">
            <aside className="hidden lg:block w-[280px] shrink-0">
              <div className="sticky top-24">
                <div className="mb-6">
                  <h2 className="text-[11px] uppercase tracking-[0.22em] text-foreground font-default font-medium">
                    Filter Devices
                  </h2>
                  <div className="mt-3 h-px w-full bg-border" />
                </div>
                <ProductsFilter />
              </div>
            </aside>

            <div className="flex-1 min-w-0">
              <div className="mb-8 flex items-center justify-between gap-4 border-b border-border pb-5">
                <div className="lg:hidden">
                  <Sheet>
                    <SheetTrigger asChild>
                      <Button variant="outline" className="rounded-lg border-border text-foreground font-default text-sm">
                        Filters
                        <Plus className="size-4" />
                      </Button>
                    </SheetTrigger>
                    <SheetContent side="left" className="w-[320px] sm:w-[360px]">
                      <SheetHeader>
                        <SheetTitle className="font-default text-2xl text-foreground">
                          Refine Selection
                        </SheetTitle>
                      </SheetHeader>
                      <div className="px-4 pb-6 overflow-y-auto">
                        <ProductsFilter />
                      </div>
                    </SheetContent>
                  </Sheet>
                </div>

                <div className="ml-auto">
                  <ProductSortDropdown label="Sort by" />
                </div>
              </div>

              <ProductsGridConsumer />
            </div>
          </div>
        </EcommerceProductsProvider>
      </div>
    </section>;
}